import { useEffect, Dispatch, SetStateAction, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Spinner, Typography } from '@scale/scaleui';
import { CreateWorkspaceResponse } from '@scale/shared/sphereEngine/types';
/* eslint-disable restrict-imports/restrict-imports */
import { Toolbar } from '@material-ui/core';
import Kbd from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ui/Kbd';

type WorkspaceConfig = CreateWorkspaceResponse;

interface WorkspaceDisplayModalProps {
  workspaceIsOpen: boolean;
  workspaceInstanceId: string;
  workspaceConfig: WorkspaceConfig;
  afterExecutionWaitTime: number;
  isLoading: boolean;
  isSubscribed: boolean;
  blockSubmissionOnFailedScenario: boolean;
  isFetchingFiles: boolean;
  setWorkspaceIsOpen: Dispatch<SetStateAction<boolean>>;
  handleWorkspaceDemount: () => Promise<void>;
  handleRefreshWorkspace: () => void;
}

const WorkspaceDisplayModal = ({
  workspaceIsOpen,
  workspaceInstanceId,
  workspaceConfig,
  afterExecutionWaitTime,
  isLoading,
  isSubscribed,
  blockSubmissionOnFailedScenario,
  isFetchingFiles,
  setWorkspaceIsOpen,
  handleWorkspaceDemount,
  handleRefreshWorkspace,
}: WorkspaceDisplayModalProps) => {
  const handleClickOutsideVisibilityChange = (e: any) => {
    if (e.target === e.currentTarget) {
      setWorkspaceIsOpen(false);
    }
  };

  const handleVisibilityKeyPress = useCallback(
    (e: any) => {
      // CTRL + M or CMD + M
      if ((e.metaKey || e.ctrlKey) && (e.key === 'm' || e.key === 'M')) {
        setWorkspaceIsOpen(prev => !prev);
      }
    },
    [setWorkspaceIsOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleVisibilityKeyPress);

    return () => {
      document.removeEventListener('keydown', handleVisibilityKeyPress);
    };
  }, [handleVisibilityKeyPress]);

  const disableExitWorkspace = afterExecutionWaitTime > 0 || isFetchingFiles;

  return (
    <div className={workspaceIsOpen ? 'visible' : 'invisible'}>
      <div
        className="fixed top-0 left-0 w-full h-full bg-opacity-40 bg-neutral-1000 flex justify-center items-center z-[1000] flex-col rounded-sm"
        onClick={handleClickOutsideVisibilityChange}
      >
        <Toolbar className="flex flex-row w-4/5 bg-neutral-0">
          <div className="flex flex-row w-full align-center items-center">
            <img
              src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png"
              alt="Outlier"
              className="w-8 h-8 mr-1"
            />
            <div className="flex font-mono font-light p-2 pr-4 w-160">
              <Typography.Paragraph1 className="text-xs">
                NOTE: Click outside the IDE to hide it.
              </Typography.Paragraph1>
            </div>
          </div>
          <div className="flex flex-row gap-1 ml-1 pr-1">
            <Button
              className="flex flex-row w-48"
              onClick={() => setWorkspaceIsOpen(false)}
              size="sm"
              variant="outline"
            >
              <FontAwesomeIcon className="mb-2" icon={'window-minimize'}></FontAwesomeIcon>
              <span className="text-xs pr-3 pl-2"> Hide </span>
              <Kbd>CTRL/CMD</Kbd> + <Kbd>M</Kbd>
            </Button>
          </div>
          <div className="flex flex-row gap-1 ml-1 pr-1">
            <Button
              className="flex flex-row w-36 h-9"
              onClick={handleRefreshWorkspace}
              size="sm"
              variant="outline"
            >
              <FontAwesomeIcon icon={'redo'}></FontAwesomeIcon>
              <span className="text-xs pl-2">Refresh Session</span>
            </Button>
          </div>
          <div className="flex flex-row gap-1 ml-1">
            <Button
              className={'flex flex-row w-36 h-9'}
              onClick={handleWorkspaceDemount}
              size="sm"
              variant="outline"
              disabled={disableExitWorkspace}
            >
              {!disableExitWorkspace && <FontAwesomeIcon icon={'window-close'}></FontAwesomeIcon>}
              <span className="text-xs pl-2">
                {disableExitWorkspace ? `Saving Files & Output...` : 'Exit Workspace'}
              </span>
            </Button>
          </div>
        </Toolbar>

        {blockSubmissionOnFailedScenario && isLoading && !isSubscribed && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-neutral-0 bg-opacity-30 flex justify-center items-center z-[1001]">
            <Spinner className="" size="sm" variant="primary" />
          </div>
        )}

        <div className="bg-white w-4/5 relative">
          <div className="h-[80vh]">
            <div
              className={'se-workspace'}
              data-id={workspaceInstanceId}
              data-workspace={workspaceConfig.workspaceId}
              data-workspace-token={workspaceConfig.workspaceToken}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceDisplayModal;
